import React, { useEffect, useState } from 'react';
import { X, Clock, User, CheckCircle2, RotateCcw, XCircle, Upload, Lock, Unlock, MessageSquare, Loader2, Activity, Send, AtSign, Film, Image as ImageIcon } from 'lucide-react';
import MentionAutocomplete from '@/components/MentionAutocomplete';
import { useAuth } from '@/contexts/AuthContext';
import { displayNameFor, shouldRenderHandlePill, handleFor } from '@/lib/displayName';

/**
 * AssetAuditTrailModal — chronological per-asset journey
 *
 *   Renders the entire history of an asset across every pipeline stage:
 *     - Stage strip at top: ✅ Concept · 🟡 Modeling · 🔒 Texturing · 🔒 Rigging
 *     - "Next Action Owner" pill: Awaiting client / With artist / Needs allotment
 *     - Vertical timeline of events (oldest at top): submitted v1 → rejected → reworked → v2 → approved → next stage unlocked → …
 *     - Each entry has timestamp + actor name + role + note
 *
 *  Backed by GET /api/assets/{asset_id}/audit-trail
 */
export default function AssetAuditTrailModal({ assetId, api, onClose }) {
  const { user } = useAuth();
  const viewerRole = user?.role;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [chat, setChat] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!assetId) return;
    api.get(`/assets/${assetId}/audit-trail`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Failed to load'));
  }, [assetId]);

  // Asset chat thread — separate from the audit timeline (the audit timeline
  // shows decisions/submissions; chat is for free-form team conversation
  // tied to this exact asset).
  const refreshChat = async () => {
    if (!assetId) return;
    try {
      const r = await api.get('/messages/thread', {
        params: { context_type: 'asset', context_id: assetId },
      });
      setChat(r.data?.messages || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { refreshChat(); }, [assetId]);
  // Auto-poll while modal is open. Perf rewrite May 24, 2026: 4s → 15s,
  // skipped when tab hidden. Re-fires instantly on focus return.
  useEffect(() => {
    if (!assetId) return;
    const tick = () => { if (!document.hidden) refreshChat(); };
    const t = setInterval(tick, 15000);
    const onVis = () => { if (!document.hidden) refreshChat(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [assetId]);

  const sendChat = async () => {
    const txt = draft.trim();
    if (!txt || !assetId) return;
    setSending(true);
    try {
      await api.post('/messages/post', {
        context_type: 'asset',
        context_id: assetId,
        project_id: data?.asset?.project_id,
        body: txt,
      });
      setDraft('');
      await refreshChat();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  if (!assetId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="asset-audit-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-md w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b border-zinc-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-violet-300 mb-0.5 flex items-center gap-1.5">
              <Activity className="w-3 h-3" /> Asset journey
            </p>
            <h2 className="text-lg font-bold text-white truncate" data-testid="audit-asset-name">
              {data?.asset?.client_display_name || data?.asset?.name || 'Loading…'}
            </h2>
            {data?.asset && (
              <p className="text-[11px] text-zinc-500">
                {data.asset.code} · {data.asset.category}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="audit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {err && (
            <div className="m-5 p-3 rounded bg-rose-500/10 border border-rose-500/40 text-xs text-rose-200">
              {err}
            </div>
          )}
          {!data && !err && (
            <div className="p-12 flex items-center justify-center text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading journey…
            </div>
          )}
          {data && (
            <>
              {/* Stage strip */}
              <div className="p-4 border-b border-zinc-800/60 bg-zinc-950/40" data-testid="audit-stage-strip">
                <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Pipeline</p>
                <div className="flex items-center gap-1 flex-wrap">
                  {data.stage_strip?.map((s, i) => (
                    <React.Fragment key={s.task_id || i}>
                      {i > 0 && <span className="text-zinc-700 text-xs">→</span>}
                      <StagePill stage={s.stage} status={s.status} progress={s.progress} />
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Next owner pill */}
              {data.next_owner && (
                <div className="px-5 py-3 border-b border-zinc-800/60 bg-amber-500/5" data-testid="audit-next-owner">
                  <div className="flex items-start gap-2.5">
                    <NextOwnerBadge owner={data.next_owner} />
                    {data.next_owner.hint && (
                      <p className="text-[11px] text-zinc-300 leading-relaxed flex-1 mt-0.5">{data.next_owner.hint}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="p-5">
                <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-3">Chronological journey</p>
                {data.entries?.length === 0 ? (
                  <p className="text-xs text-zinc-500 italic">No activity yet — pipeline tasks were just created and are waiting to be allotted.</p>
                ) : (
                  <ol className="relative border-l-2 border-zinc-800 ml-3 space-y-4">
                    {data.entries?.map((e, i) => (
                      <TimelineEntry key={i} e={e} api={api} viewerRole={viewerRole} />
                    ))}
                  </ol>
                )}
              </div>
              {/* Asset-scoped chat — messages live inside the work */}
              <div className="border-t border-zinc-800 bg-zinc-950/40 p-4" data-testid="audit-chat-panel">
                <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> Chat about this asset
                  <span className="text-zinc-600 font-mono ml-1">({chat.length})</span>
                </p>
                <div className="max-h-48 overflow-y-auto space-y-2 mb-2.5 px-1" data-testid="audit-chat-messages">
                  {chat.length === 0 && (
                    <p className="text-[11px] text-zinc-600 italic">No chat yet — be the first to ping the team. Tag with @name.</p>
                  )}
                  {chat.map(m => (
                    <div key={m.id} className="text-[11px] leading-relaxed">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="font-semibold text-zinc-200">{displayNameFor(m, viewerRole)}</span>
                        {shouldRenderHandlePill(m, viewerRole) && (
                          <span className="text-cyan-400 font-mono text-[10px]">@{handleFor(m)}</span>
                        )}
                        <span className="text-[9px] text-zinc-500">
                          {m.author_role?.replace(/_/g, ' ')} · {new Date(m.created_at).toLocaleString('en', {day:'numeric', month:'short', hour:'numeric', minute:'2-digit'})}
                        </span>
                      </div>
                      <p className="text-zinc-300">{renderMentionAware(m.body)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <MentionAutocomplete
                      api={api}
                      projectId={data?.asset?.project_id}
                      value={draft}
                      onChange={setDraft}
                      dataTestid="audit-chat-input"
                      onEnterSubmit={sendChat}
                      placeholder="Reply… use @name to tag the team"
                      rows={1}
                    />
                  </div>
                  <button onClick={sendChat} disabled={sending || !draft.trim()}
                    data-testid="audit-chat-send"
                    className="h-8 px-3 rounded-md bg-violet-500 hover:bg-violet-400 text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40">
                    {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Send
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const StagePill = ({ stage, status, progress }) => {
  const cfg = (() => {
    switch (status) {
      case 'completed':   return { icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' };
      case 'in_progress': return { icon: Activity,     cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' };
      case 'in_review':   return { icon: Clock,        cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40' };
      case 'blocked':     return { icon: Lock,         cls: 'bg-zinc-500/10 text-zinc-500 border-zinc-700' };
      case 'unassigned':  return { icon: Unlock,       cls: 'bg-violet-500/15 text-violet-300 border-violet-500/40' };
      default:            return { icon: Clock,        cls: 'bg-zinc-800 text-zinc-400 border-zinc-700' };
    }
  })();
  const Icon = cfg.icon;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border flex items-center gap-1 ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      <span>{(stage || '').replace(/_/g, ' ')}</span>
      {progress > 0 && progress < 100 && (
        <span className="text-[9px] font-mono opacity-80">{progress}%</span>
      )}
    </span>
  );
};

const NextOwnerBadge = ({ owner }) => {
  const colorMap = {
    client:     'bg-violet-500/20 text-violet-200 border-violet-500/40',
    artist:     'bg-amber-500/20 text-amber-200 border-amber-500/40',
    pm:         'bg-cyan-500/20 text-cyan-200 border-cyan-500/40',
    supervisor: 'bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-500/40',
    blocked:    'bg-zinc-700 text-zinc-300 border-zinc-600',
    done:       'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
    unknown:    'bg-zinc-800 text-zinc-400 border-zinc-700',
  };
  const cls = colorMap[owner.role] || colorMap.unknown;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border font-bold ${cls} flex-shrink-0`}>
      Next: {owner.label}
    </span>
  );
};

const TimelineEntry = ({ e, api, viewerRole }) => {
  const cfg = (() => {
    switch (e.kind) {
      case 'submission':         return { icon: Upload,        color: 'text-cyan-300',    bg: 'bg-cyan-500' };
      case 'client_approved':    return { icon: CheckCircle2,  color: 'text-emerald-300', bg: 'bg-emerald-500' };
      case 'client_revise':      return { icon: RotateCcw,     color: 'text-rose-300',    bg: 'bg-rose-500' };
      case 'client_rejected':    return { icon: XCircle,       color: 'text-red-300',     bg: 'bg-red-500' };
      case 'comment':            return { icon: MessageSquare, color: 'text-amber-300',   bg: 'bg-amber-500' };
      case 'task_completed':     return { icon: CheckCircle2,  color: 'text-emerald-300', bg: 'bg-emerald-600' };
      case 'stage_unlocked':     return { icon: Unlock,        color: 'text-violet-300',  bg: 'bg-violet-500' };
      default:                   return { icon: Clock,         color: 'text-zinc-400',    bg: 'bg-zinc-600' };
    }
  })();
  const Icon = cfg.icon;
  const isSubmission = e.kind === 'submission';
  const mediaList = Array.isArray(e.media) ? e.media : [];
  return (
    <li className="ml-6 relative" data-testid={`timeline-${e.kind}-${e.round || ''}`}>
      <span className={`absolute -left-[34px] top-1 w-5 h-5 rounded-full ${cfg.bg} flex items-center justify-center ring-4 ring-zinc-900`}>
        <Icon className="w-3 h-3 text-white" />
      </span>
      <div className="text-[11px] text-zinc-500 mb-0.5">
        <span className="font-mono">{fmtTs(e.at)}</span>
        {e.actor_name && (
          <>
            <span className="mx-1.5">·</span>
            <span className="text-zinc-300">{displayNameFor(e, viewerRole)}</span>
          </>
        )}
        {e.actor_role && (
          <>
            <span className="mx-1.5">·</span>
            <span className="text-zinc-500">{e.actor_role.replace(/_/g, ' ')}</span>
          </>
        )}
        {e.is_history && (
          <span className="ml-2 text-[9px] uppercase tracking-wider text-zinc-600 bg-zinc-800/60 rounded px-1 py-0.5">archived round</span>
        )}
      </div>
      <p className={`text-sm font-medium ${cfg.color}`}>
        {e.summary}
        {e.round && isSubmission && (
          <span className="ml-2 text-[10px] uppercase tracking-wider font-mono text-zinc-500">v{e.round}</span>
        )}
      </p>
      {e.note && (
        <p className="mt-1 px-2.5 py-1.5 rounded bg-zinc-800/60 border border-zinc-800 text-[12px] text-zinc-300 italic">
          "{e.note}"
        </p>
      )}
      {/* Per-round media strip — renders thumbnails so the artist + client
          can see exactly what was uploaded in v1, v2, v3, etc. */}
      {isSubmission && mediaList.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`round-media-${e.round}`}>
          {mediaList.slice(0, 8).map((m, i) => (
            <RoundThumb key={i} media={m} api={api} />
          ))}
          {mediaList.length > 8 && (
            <span className="text-[10px] text-zinc-500 self-center">+{mediaList.length - 8}</span>
          )}
        </div>
      )}
    </li>
  );
};

// Tiny authenticated thumbnail used inside the audit timeline. Falls back to
// a typed icon if storage_key is missing or the media stream fails.
const RoundThumb = ({ media, api }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!media?.storage_key || !api) return;
    let cancelled = false;
    let blobUrl = '';
    (async () => {
      try {
        const r = await api.get(`/reviews/media?key=${encodeURIComponent(media.storage_key)}`, { responseType: 'blob' });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(r.data);
        setUrl(blobUrl);
      } catch { /* fallback to icon */ }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [media?.storage_key]);
  const isVid = (media?.type || '').startsWith('video');
  return (
    <div className="relative w-14 h-14 bg-zinc-900 border border-zinc-800 rounded overflow-hidden flex items-center justify-center"
         title={`${media?.filename || 'file'} · ${(media?.size || 0) > 1024 ? Math.round((media?.size || 0) / 1024) + 'KB' : (media?.size || 0) + 'B'}`}>
      {url ? (
        isVid ? (
          <video src={url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        ) : (
          <img src={url} alt={media?.filename || ''} className="w-full h-full object-cover" />
        )
      ) : (
        isVid ? <Film className="w-4 h-4 text-zinc-600" /> : <ImageIcon className="w-4 h-4 text-zinc-600" />
      )}
      {isVid && url && (
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Film className="w-4 h-4 text-white/70 drop-shadow" />
        </span>
      )}
    </div>
  );
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return iso; }
};

// Render text with @mentions visually highlighted as chips
function renderMentionAware(text) {
  if (!text) return '';
  const re = /@([A-Za-z0-9][A-Za-z0-9._\-]{1,40})/g;
  const parts = [];
  let last = 0; let m; let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={`mc-${i++}`} className="bg-violet-500/30 text-violet-100 rounded px-1 font-medium">
        @{m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}
